import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Home as HomeIcon, FileText, Settings, LogOut, Eye, MessageCircle, Heart, Pause, Play, Edit, Trash2, Crown, Download, Plus, AlertCircle, ChevronRight, TrendingUp, Calendar, Sparkles, Mail, Inbox, ShieldCheck, Compass } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { PLANS } from '../mockPlans';
import { formatCOP } from '../mock';
import { api, imgUrl, friendlyError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import AvatarUploader from '../components/AvatarUploader';
import { FormError, FormBanner } from '../components/FormError';
import useFormValidation from '../hooks/useFormValidation';

const TABS = [
  { id: 'overview', label: 'Resumen', icon: TrendingUp },
  { id: 'properties', label: 'Mis publicaciones', icon: HomeIcon },
  { id: 'favorites', label: 'Favoritos', icon: Heart },
  { id: 'inbox', label: 'Mensajes', icon: Inbox },
  { id: 'inspections', label: 'Visitas', icon: Calendar },
  { id: 'plan', label: 'Mi plan', icon: Crown },
  { id: 'billing', label: 'Facturación', icon: FileText },
  { id: 'settings', label: 'Configuración', icon: Settings }
];

export default function MyAccount() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, refresh } = useAuth();
  const { toast } = useToast();
  const initialTab = (() => {
    const t = searchParams.get('tab');
    return TABS.find(x => x.id === t) ? t : 'overview';
  })();
  const [tab, setTab] = useState(initialTab);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [properties, setProperties] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleTabChange = (id) => {
    setTab(id);
    const next = new URLSearchParams(searchParams);
    if (id === 'overview') next.delete('tab');
    else next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  // Keep tab state in sync with URL (e.g. back/forward navigation)
  useEffect(() => {
    const t = searchParams.get('tab');
    const valid = t && TABS.find(x => x.id === t);
    setTab(valid ? t : 'overview');
  }, [searchParams]);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [sub, inv, props, msgs, ins, fav] = await Promise.all([
          api.currentSubscription().catch(() => ({ subscription: null })),
          api.invoices().catch(() => ({ items: [] })),
          api.myProperties().catch(() => ({ items: [] })),
          api.myMessages().catch(() => ({ items: [] })),
          api.myInspections().catch(() => ({ items: [] })),
          api.listFavorites().catch(() => ({ items: [] })),
        ]);
        setSubscription(sub.subscription);
        setInvoices(inv.items || []);
        setProperties(props.items || []);
        setMessages(msgs.items || []);
        setInspections(ins.items || []);
        setFavorites(fav.items || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (!user) return null;

  const currentPlanId = user.plan_id || 'gratis';
  const currentPlan = PLANS.find(p => p.id === currentPlanId) || PLANS[0];
  const planLimit = typeof currentPlan.limits.properties === 'number' ? currentPlan.limits.properties : 999;
  const activeCount = properties.filter(p => p.status === 'activa').length;
  const usagePct = Math.min(100, (activeCount / planLimit) * 100);

  const togglePropStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'activa' ? 'pausada' : 'activa';
    try {
      await api.setPropertyStatus(id, newStatus);
      setProperties(properties.map(p => p.id === id ? { ...p, status: newStatus } : p));
    } catch (err) {
      toast({ title: 'No pudimos actualizar el estado', description: friendlyError(err), variant: 'destructive' });
    }
  };

  const deleteProp = async (id) => {
    if (!confirm('¿Eliminar esta publicación?')) return;
    try {
      await api.deleteProperty(id);
      setProperties(properties.filter(p => p.id !== id));
      toast({ title: 'Publicación eliminada' });
    } catch (err) {
      toast({ title: 'No pudimos eliminar la publicación', description: friendlyError(err), variant: 'destructive' });
    }
  };

  const cancelSub = async () => {
    if (!confirm('¿Cancelar tu suscripción? Volverás al plan Gratis.')) return;
    try {
      await api.cancelSubscription();
      await refresh();
      const sub = await api.currentSubscription();
      setSubscription(sub.subscription);
      toast({ title: 'Suscripción cancelada' });
    } catch (err) {
      toast({ title: 'No pudimos cancelar tu suscripción', description: friendlyError(err), variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen bg-habita-cream">
      <Header />

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-10">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8">
          <aside>
            <div className="bg-white rounded-2xl p-5 border border-gray-200">
              <div className="flex items-center gap-3">
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-habita-mint" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-habita-mint flex items-center justify-center text-habita font-bold ring-2 ring-habita-mint">{user.name?.[0]?.toUpperCase()}</div>
                )}
                <div className="min-w-0">
                  <p className="font-semibold text-habita-dark truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-habita-mint rounded-xl">
                <p className="text-[10px] uppercase tracking-widest text-habita font-semibold">Plan actual</p>
                <p className="font-serif text-xl font-bold text-habita-dark mt-0.5 flex items-center gap-1">
                  <Crown className="w-4 h-4" /> {currentPlan.name}
                </p>
                {currentPlan.id !== 'gratis' && (
                  <p className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider mt-1">Acceso beta gratuito</p>
                )}
              </div>
            </div>
            <nav className="mt-3 bg-white rounded-2xl border border-gray-200 p-2">
              {TABS.map(t => {
                const active = tab === t.id;
                return (
                  <button key={t.id} onClick={() => handleTabChange(t.id)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${active ? 'bg-habita text-white' : 'text-gray-700 hover:bg-gray-100'}`} data-testid={`myaccount-tab-${t.id}`}>
                    <t.icon className="w-4 h-4" /> {t.label}
                    {t.id === 'inbox' && messages.filter(m => !m.read).length > 0 && (
                      <span className="ml-auto bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{messages.filter(m => !m.read).length}</span>
                    )}
                  </button>
                );
              })}
              <Link to="/mi-cuenta/privacidad" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-100 mt-2 pt-2 border-t border-gray-100" data-testid="myaccount-privacy-link">
                <ShieldCheck className="w-4 h-4" /> Centro de Privacidad
              </Link>
              <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-rose-600 hover:bg-rose-50">
                <LogOut className="w-4 h-4" /> Cerrar sesión
              </button>
            </nav>
          </aside>

          <main>
            {loading ? (
              <div className="bg-white rounded-2xl p-20 text-center border border-gray-200">
                <div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin mx-auto" />
              </div>
            ) : (
              <>
                {tab === 'overview' && (
                  <div className="space-y-6">
                    <div className="flex items-end justify-between flex-wrap gap-3">
                      <div>
                        <h1 className="font-serif text-3xl font-bold text-habita-dark">Hola, {user.name?.split(' ')[0]} 👋</h1>
                        <p className="text-gray-600 mt-1">Aquí tienes el resumen de tu actividad.</p>
                      </div>
                      <Button onClick={() => navigate('/publicar')} className="btn-habita rounded-full px-6 h-11"><Plus className="w-4 h-4 mr-1" /> Nueva publicación</Button>
                    </div>

                    <div className="grid md:grid-cols-3 gap-4">
                      {[
                        { icon: HomeIcon, label: 'Publicaciones activas', value: activeCount, sub: `de ${currentPlan.limits.properties} permitidas` },
                        { icon: Eye, label: 'Vistas totales', value: properties.reduce((a, b) => a + (b.views || 0), 0).toLocaleString('es-CO'), sub: 'en todas tus propiedades' },
                        { icon: MessageCircle, label: 'Mensajes recibidos', value: messages.length, sub: 'inbox total' }
                      ].map((s, i) => (
                        <div key={i} className="bg-white rounded-2xl p-5 border border-gray-200">
                          <div className="w-10 h-10 rounded-xl bg-habita-mint flex items-center justify-center"><s.icon className="w-5 h-5 text-habita" /></div>
                          <p className="font-serif text-3xl font-bold text-habita-dark mt-4">{s.value}</p>
                          <p className="text-sm font-medium text-gray-700 mt-1">{s.label}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{s.sub}</p>
                        </div>
                      ))}
                    </div>

                    <div className="bg-white rounded-2xl p-6 border border-gray-200">
                      <div className="flex justify-between items-baseline mb-3">
                        <div>
                          <p className="font-semibold text-habita-dark">Uso de tu plan</p>
                          <p className="text-xs text-gray-500 mt-0.5">{activeCount} de {currentPlan.limits.properties} publicaciones activas</p>
                        </div>
                        {activeCount >= planLimit && currentPlan.id !== 'inmobiliaria' && (
                          <Link to="/precios" className="text-sm font-semibold text-amber-600 hover:underline flex items-center gap-1">Mejorar plan <ChevronRight className="w-4 h-4" /></Link>
                        )}
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-habita transition-all duration-700" style={{ width: `${usagePct}%` }} />
                      </div>
                    </div>
                  </div>
                )}

                {tab === 'properties' && (
                  <div>
                    <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                      <div>
                        <h1 className="font-serif text-3xl font-bold text-habita-dark">Mis publicaciones</h1>
                        <p className="text-sm text-gray-600 mt-1">Gestiona tus propiedades y controla su visibilidad en el Advisor.</p>
                      </div>
                      <Button onClick={() => navigate('/publicar')} className="btn-habita rounded-full h-11 px-5" data-testid="myaccount-new-property-btn"><Plus className="w-4 h-4 mr-1" /> Nueva</Button>
                    </div>

                    {/* Aviso preventivo: usó todas sus publicaciones disponibles */}
                    {activeCount >= planLimit && currentPlan.id !== 'inmobiliaria' && currentPlan.id !== 'constructora' && (
                      <div className="mb-6 flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="plan-limit-banner">
                        <p className="text-sm text-amber-900">
                          Has usado <strong>{activeCount} de {planLimit}</strong> publicaci{planLimit === 1 ? 'ón' : 'ones'}. Mejora tu plan gratis para publicar más.
                        </p>
                        <Link to="/precios" data-testid="plan-limit-upgrade-link" className="shrink-0 text-sm font-semibold text-amber-700 hover:underline inline-flex items-center gap-1">
                          Mejorar plan <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    )}

                    {properties.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="properties-stats">
                        {[
                          { label: 'Total', value: properties.length, tint: 'bg-gray-100 text-habita-dark' },
                          { label: 'Activas · en Advisor', value: properties.filter(p => p.status === 'activa').length, tint: 'bg-habita-mint text-habita' },
                          { label: 'En revisión', value: properties.filter(p => p.status === 'pendiente').length, tint: 'bg-amber-100 text-amber-700' },
                          { label: 'Pausadas', value: properties.filter(p => p.status === 'pausada').length, tint: 'bg-gray-100 text-gray-700' },
                        ].map((s, i) => (
                          <div key={i} className={`rounded-2xl px-4 py-3 ${s.tint}`}>
                            <p className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{s.label}</p>
                            <p className="font-serif text-2xl font-bold mt-0.5">{s.value}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {properties.length === 0 ? (
                      <div className="bg-white rounded-2xl p-16 text-center border border-gray-200">
                        <HomeIcon className="w-12 h-12 text-gray-300 mx-auto" />
                        <p className="mt-4 text-gray-600">Aún no tienes publicaciones</p>
                        <Button onClick={() => navigate('/publicar')} className="btn-habita mt-4 rounded-full">Publicar mi primera propiedad</Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {properties.map(p => {
                          const inAdvisor = p.status === 'activa';
                          const searchHref = `/buscar?op=${encodeURIComponent(p.operation || '')}&city=${encodeURIComponent(p.city || '')}&type=${encodeURIComponent(p.type || '')}`;
                          return (
                          <div key={p.id} className="bg-white rounded-2xl p-4 border border-gray-200 flex flex-col md:flex-row gap-4" data-testid={`myprop-row-${p.id}`}>
                            <Link to={`/propiedad/${p.id}`} className="block w-full md:w-48 h-32 rounded-xl overflow-hidden bg-gray-100">
                              <img src={imgUrl(p.images?.[0]) || 'https://via.placeholder.com/200x150'} alt="" className="w-full h-full object-cover" />
                            </Link>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${p.status === 'activa' ? 'bg-habita-mint text-habita' : p.status === 'pausada' ? 'bg-amber-100 text-amber-700' : p.status === 'pendiente' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`} data-testid={`myprop-status-${p.id}`}>{p.status}</span>
                                    {inAdvisor ? (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-habita" title="Aparece en las recomendaciones del Habita Advisor" data-testid={`myprop-advisor-badge-${p.id}`}>
                                        <Compass className="w-3 h-3" /> En Advisor
                                      </span>
                                    ) : p.status === 'pendiente' ? (
                                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-amber-700" title="Aparecerá cuando se apruebe">
                                        <Compass className="w-3 h-3" /> Se sumará al aprobarse
                                      </span>
                                    ) : null}
                                  </div>
                                  <h3 className="font-serif text-lg font-bold text-habita-dark mt-1 line-clamp-1">{p.title}</h3>
                                  <p className="text-xs text-gray-500">{p.address || p.neighborhood || ''} · {p.city}</p>
                                </div>
                                <p className="font-serif text-xl font-bold text-habita whitespace-nowrap">{formatCOP(p.price)}</p>
                              </div>
                              <div className="flex items-center gap-5 mt-3 text-xs text-gray-600 flex-wrap">
                                <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {p.views || 0} vistas</span>
                                <span className="flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {p.contacts || 0} contactos</span>
                                <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {p.favorites_count || 0} favoritos</span>
                              </div>
                              <div className="flex items-center gap-2 mt-4 flex-wrap">
                                <button onClick={() => navigate(`/editar/${p.id}`)} data-testid={`edit-prop-${p.id}`} className="text-xs font-semibold px-3 h-8 rounded-full bg-habita text-white hover:bg-habita-dark flex items-center gap-1">
                                  <Edit className="w-3 h-3" /> Editar
                                </button>
                                <button onClick={() => togglePropStatus(p.id, p.status)} data-testid={`toggle-prop-${p.id}`} className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-200 hover:border-habita flex items-center gap-1">
                                  {p.status === 'activa' ? <><Pause className="w-3 h-3" /> Pausar</> : <><Play className="w-3 h-3" /> Activar</>}
                                </button>
                                <Link to={`/propiedad/${p.id}`} className="text-xs font-semibold px-3 h-8 rounded-full border border-gray-200 hover:border-habita flex items-center gap-1"><Eye className="w-3 h-3" /> Ver</Link>
                                {inAdvisor && (
                                  <Link to={searchHref} data-testid={`view-in-advisor-${p.id}`} className="text-xs font-semibold px-3 h-8 rounded-full bg-habita-mint text-habita hover:bg-habita hover:text-white transition-colors flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" /> Ver en Advisor
                                  </Link>
                                )}
                                <button onClick={() => navigate('/checkout/destacar?type=addon')} className="text-xs font-semibold px-3 h-8 rounded-full bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Destacar</button>
                                <button onClick={() => deleteProp(p.id)} data-testid={`delete-prop-${p.id}`} className="text-xs font-semibold px-3 h-8 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 ml-auto flex items-center gap-1"><Trash2 className="w-3 h-3" /> Eliminar</button>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'favorites' && (
                  <div>
                    <h1 className="font-serif text-3xl font-bold text-habita-dark mb-6">Mis favoritos</h1>
                    {favorites.length === 0 ? (
                      <div className="bg-white rounded-2xl p-16 text-center border border-gray-200">
                        <Heart className="w-12 h-12 text-gray-300 mx-auto" />
                        <p className="mt-4 text-gray-600">Aún no tienes propiedades guardadas</p>
                        <Button onClick={() => navigate('/buscar')} className="btn-habita mt-4 rounded-full">Explorar propiedades</Button>
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {favorites.map(p => (
                          <div key={p.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover-lift relative">
                            <Link to={`/propiedad/${p.id}`} className="block">
                              <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                                <img src={imgUrl(p.images?.[0])} alt="" className="w-full h-full object-cover" />
                              </div>
                              <div className="p-4">
                                <p className="font-serif text-xl font-bold text-habita">{formatCOP(p.price)}{p.operation === 'arriendo' && <span className="text-xs font-normal text-gray-500"> /mes</span>}</p>
                                <p className="text-sm font-medium text-gray-900 mt-1 line-clamp-1">{p.title}</p>
                                <p className="text-xs text-gray-500">{p.city}</p>
                              </div>
                            </Link>
                            <button onClick={async () => { await api.removeFavorite(p.id); setFavorites(favorites.filter(f => f.id !== p.id)); }} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/95 hover:bg-rose-50 flex items-center justify-center">
                              <Heart className="w-4 h-4 fill-rose-500 text-rose-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'inbox' && (
                  <div>
                    <h1 className="font-serif text-3xl font-bold text-habita-dark mb-6">Mensajes recibidos</h1>
                    {messages.length === 0 ? (
                      <div className="bg-white rounded-2xl p-16 text-center border border-gray-200">
                        <Mail className="w-12 h-12 text-gray-300 mx-auto" />
                        <p className="mt-4 text-gray-600">No tienes mensajes todavía</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {messages.map(m => (
                          <div key={m.id} className="bg-white rounded-2xl p-5 border border-gray-200">
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-semibold text-habita-dark">{m.name}</p>
                                <p className="text-xs text-gray-500">{m.email} · {m.phone}</p>
                              </div>
                              <p className="text-xs text-gray-400">{new Date(m.created_at).toLocaleDateString('es-CO')}</p>
                            </div>
                            <p className="text-sm text-gray-700 mt-3">{m.message}</p>
                            <Link to={`/propiedad/${m.property_id}`} className="text-xs text-habita font-semibold mt-3 inline-flex items-center gap-1">Ver propiedad <ChevronRight className="w-3 h-3" /></Link>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'inspections' && (
                  <div>
                    <h1 className="font-serif text-3xl font-bold text-habita-dark mb-6">Visitas agendadas</h1>
                    {inspections.length === 0 ? (
                      <div className="bg-white rounded-2xl p-16 text-center border border-gray-200">
                        <Calendar className="w-12 h-12 text-gray-300 mx-auto" />
                        <p className="mt-4 text-gray-600">No hay visitas agendadas</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {inspections.map(ins => (
                          <div key={ins.id} className="bg-white rounded-2xl p-5 border border-gray-200 flex items-start gap-4">
                            <div className="w-14 h-14 rounded-xl bg-habita-mint flex flex-col items-center justify-center shrink-0">
                              <span className="text-[10px] text-habita uppercase font-semibold">{new Date(ins.date).toLocaleDateString('es-CO', { month: 'short' })}</span>
                              <span className="text-xl font-bold text-habita-dark leading-none">{new Date(ins.date).getDate()}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-habita-dark">{ins.property_title || 'Propiedad'}</p>
                              <p className="text-xs text-gray-500">{ins.property_address}</p>
                              <p className="text-sm text-gray-700 mt-1">{ins.time}</p>
                              <p className="text-xs text-gray-500 mt-2">{ins.name} · {ins.email} · {ins.phone}</p>
                              {ins.notes && <p className="text-xs text-gray-600 italic mt-1">&ldquo;{ins.notes}&rdquo;</p>}
                            </div>
                            <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded bg-habita-mint text-habita">{ins.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {tab === 'plan' && (
                  <div className="space-y-6">
                    <h1 className="font-serif text-3xl font-bold text-habita-dark">Mi plan</h1>
                    <div className="bg-habita-dark rounded-3xl p-8 text-white relative overflow-hidden">
                      <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-amber-500/20 blur-3xl" />
                      <div className="relative flex items-start justify-between flex-wrap gap-5">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs uppercase tracking-widest text-amber-300 font-semibold">Plan actual</span>
                            {currentPlan.id !== 'gratis' && (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-emerald-500/90 text-white">Acceso beta gratuito</span>
                            )}
                          </div>
                          <h2 className="font-serif text-5xl font-bold mt-2">{currentPlan.name}</h2>
                          <p className="text-white/70 mt-2">{currentPlan.description}</p>
                          {subscription && currentPlan.id !== 'gratis' && (
                            <p className="text-sm text-white/80 mt-5">
                              Acceso beta activo · <strong>Sin cobros durante la beta</strong>
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          {currentPlan.id === 'gratis' ? (
                            <>
                              <p className="font-serif text-4xl font-bold">Gratis</p>
                            </>
                          ) : (
                            <>
                              <p className="font-serif text-4xl font-bold text-emerald-300">$0</p>
                              <p className="text-xs text-white/60">durante la beta</p>
                              <p className="text-[10px] text-white/40 mt-1 line-through">{formatCOP(currentPlan.price)}/mes</p>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3 mt-7">
                        {[
                          ['Publicaciones', currentPlan.limits.properties],
                          ['Fotos', currentPlan.limits.photos],
                          ['Duración', `${currentPlan.limits.duration}d`],
                          ['Destacados', currentPlan.limits.featured]
                        ].map(([k, v]) => (
                          <div key={k} className="bg-white/10 rounded-xl p-3">
                            <p className="text-[10px] uppercase tracking-widest text-white/60">{k}</p>
                            <p className="font-serif text-xl font-bold mt-1">{v}</p>
                          </div>
                        ))}
                      </div>
                      <div className="relative flex gap-3 mt-7 flex-wrap">
                        <Button onClick={() => navigate('/precios')} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full px-6 h-11">{currentPlan.id === 'gratis' ? 'Activar acceso beta' : 'Cambiar plan'}</Button>
                        {subscription && currentPlan.id !== 'gratis' && (
                          <button onClick={cancelSub} className="px-6 h-11 rounded-full border border-white/30 hover:bg-white/10 text-sm font-semibold">Volver al plan Gratis</button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {tab === 'billing' && (
                  <div>
                    <h1 className="font-serif text-3xl font-bold text-habita-dark mb-6">Historial de facturación</h1>
                    {invoices.length === 0 ? (
                      <div className="bg-white rounded-2xl p-16 text-center border border-gray-200">
                        <FileText className="w-12 h-12 text-gray-300 mx-auto" />
                        <p className="mt-4 text-gray-600">Aún no tienes facturas</p>
                      </div>
                    ) : (
                      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden overflow-x-auto">
                        <table className="w-full text-sm min-w-[700px]">
                          <thead className="bg-habita-cream">
                            <tr>
                              <th className="text-left p-4 font-semibold text-habita-dark">Factura</th>
                              <th className="text-left p-4 font-semibold text-habita-dark">Fecha</th>
                              <th className="text-left p-4 font-semibold text-habita-dark">Plan</th>
                              <th className="text-left p-4 font-semibold text-habita-dark">Método</th>
                              <th className="text-right p-4 font-semibold text-habita-dark">Monto</th>
                              <th className="text-center p-4 font-semibold text-habita-dark">Estado</th>
                              <th className="text-right p-4"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map(inv => (
                              <tr key={inv.id} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="p-4 font-mono text-xs text-gray-600">{inv.id}</td>
                                <td className="p-4 text-gray-700">{new Date(inv.date).toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                <td className="p-4 font-medium">{inv.plan}</td>
                                <td className="p-4 text-gray-600">{inv.method}</td>
                                <td className="p-4 text-right font-semibold text-habita-dark">{formatCOP(inv.amount)}</td>
                                <td className="p-4 text-center">
                                  <span className="inline-block text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded bg-habita-mint text-habita">{inv.status}</span>
                                </td>
                                <td className="p-4 text-right"><button className="text-habita hover:underline text-sm font-medium flex items-center gap-1 ml-auto"><Download className="w-3.5 h-3.5" /> PDF</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {tab === 'settings' && (
                  <div>
                    <h1 className="font-serif text-3xl font-bold text-habita-dark mb-6">Configuración</h1>
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-5">
                      <h3 className="font-serif text-lg font-bold text-habita-dark mb-5">Foto de perfil</h3>
                      <AvatarUploader user={user} onUpdated={refresh} />
                    </div>
                    <SettingsForm user={user} refresh={refresh} />
                    <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mt-5">
                      <h3 className="font-serif text-lg font-bold text-rose-900 flex items-center gap-2"><AlertCircle className="w-5 h-5" /> Zona de peligro</h3>
                      <p className="text-sm text-rose-700 mt-2">Eliminar tu cuenta es permanente y no se puede deshacer.</p>
                      <button className="mt-4 px-5 h-10 rounded-full border border-rose-300 text-rose-700 hover:bg-rose-100 text-sm font-semibold">Eliminar cuenta</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function SettingsForm({ user, refresh }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: user.name || '', phone: user.phone || '', agency: user.agency || '', bio: user.bio || '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const v = useFormValidation();

  const setField = (k, val) => { setForm((f) => ({ ...f, [k]: val })); v.clearField(k); };

  const save = async () => {
    v.reset();
    const ok = v.validate({
      name: !form.name || form.name.trim().length < 2 ? 'El nombre debe tener al menos 2 caracteres.' : null,
      phone: form.phone && form.phone.trim().length > 0 && form.phone.trim().length < 7 ? 'Ingresa un teléfono válido.' : null,
      bio: form.bio && form.bio.length > 1000 ? 'La biografía no debe superar los 1.000 caracteres.' : null,
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.updateMe(form);
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      const first = v.applyError(err, 'No pudimos guardar los cambios.');
      if (!first) {
        toast({ title: 'No pudimos guardar los cambios', description: friendlyError(err), variant: 'destructive' });
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
      <h3 className="font-serif text-xl font-bold text-habita-dark">Datos personales</h3>
      <FormBanner msg={v.banner} testid="settings-error-banner" />
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500">Nombre</label>
          <input
            value={form.name}
            onChange={e => setField('name', e.target.value)}
            className={`mt-1.5 w-full h-11 rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 ${v.fieldErrors.name ? 'border-rose-500 focus:ring-rose-400' : 'border-input focus:ring-habita'}`}
            data-testid="settings-name-input"
            {...v.field('name')}
          />
          <FormError msg={v.fieldErrors.name} testid="settings-error-name" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500">Email</label>
          <input value={user.email} disabled className="mt-1.5 w-full h-11 rounded-md border border-input bg-gray-50 px-3 text-sm outline-none text-gray-500" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-gray-500">Teléfono</label>
          <input
            value={form.phone}
            onChange={e => setField('phone', e.target.value)}
            className={`mt-1.5 w-full h-11 rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 ${v.fieldErrors.phone ? 'border-rose-500 focus:ring-rose-400' : 'border-input focus:ring-habita'}`}
            data-testid="settings-phone-input"
            {...v.field('phone')}
          />
          <FormError msg={v.fieldErrors.phone} testid="settings-error-phone" />
        </div>
        {(user.role === 'agent' || user.role === 'agency') && (
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-500">Agencia</label>
            <input
              value={form.agency}
              onChange={e => setField('agency', e.target.value)}
              className={`mt-1.5 w-full h-11 rounded-md border bg-transparent px-3 text-sm outline-none focus:ring-2 ${v.fieldErrors.agency ? 'border-rose-500 focus:ring-rose-400' : 'border-input focus:ring-habita'}`}
              data-testid="settings-agency-input"
              {...v.field('agency')}
            />
            <FormError msg={v.fieldErrors.agency} testid="settings-error-agency" />
          </div>
        )}
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-gray-500">Bio</label>
        <textarea
          value={form.bio}
          onChange={e => setField('bio', e.target.value)}
          rows={3}
          className={`mt-1.5 w-full rounded-md border bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 ${v.fieldErrors.bio ? 'border-rose-500 focus:ring-rose-400' : 'border-input focus:ring-habita'}`}
          data-testid="settings-bio-input"
          {...v.field('bio')}
        />
        <FormError msg={v.fieldErrors.bio} testid="settings-error-bio" />
      </div>
      <Button onClick={save} disabled={saving} className="btn-habita rounded-full px-6 h-11 disabled:opacity-60" data-testid="settings-save-btn">
        {saving ? 'Guardando...' : saved ? '✓ Guardado' : 'Guardar cambios'}
      </Button>
    </div>
  );
}
